import { formatDurationMs } from './formatRunDuration';
import { PipelineRunStatus } from '../constants/pipelineRunStatus';

// AC-11/AC-11a/OQ-3 — "last N runs" stats + sparkline descriptor, computed CLIENT-SIDE off
// the already-fetched, UNPAGED GET /runs list (newest-first per the API contract). OQ-3
// resolved windowSize=10 — a fixed last-N window, never a calendar window (a calendar
// window renders a misleading empty "0 runs" strip on a healthy-but-idle pipeline).
export const RUN_BAR_STATE = {
	SUCCESS: 'success',
	ERROR: 'error',
	// AC-11a(iii) — a non-terminal run (no outcome yet) is neither success nor error.
	NEUTRAL: 'neutral'
};

// AC-11a(ii) — the explicit "no completed runs" sentinel. Never NaN / "Invalid Date".
export const NO_COMPLETED_RUNS_LABEL = '—';

const toBarState = run => {
	if (run?.Status === PipelineRunStatus.Succeeded) return RUN_BAR_STATE.SUCCESS;
	if (run?.Status === PipelineRunStatus.Failed) return RUN_BAR_STATE.ERROR;
	return RUN_BAR_STATE.NEUTRAL;
};

export const computeRunHistoryStats = (runs, windowSize = 10) => {
	const list = Array.isArray(runs) ? runs : [];
	// `runs` is newest-first and unpaged — window client-side, never assume a bounded list.
	const window = list.slice(0, windowSize);

	const windowCount = window.length;
	const succeededCount = window.filter(run => run?.Status === PipelineRunStatus.Succeeded).length;

	// AC-11a(i) — non-terminal runs (no CompletedAt) are EXCLUDED from the median but are
	// still counted in `windowCount` above.
	const completedDurationsMs = window
		.filter(run => run?.StartedAt && run?.CompletedAt)
		.map(run => new Date(run.CompletedAt).getTime() - new Date(run.StartedAt).getTime())
		.filter(ms => Number.isFinite(ms) && ms >= 0)
		.sort((a, b) => a - b);

	// AC-11a(ii) — no terminal run in the window => explicit sentinel, never NaN. Handles
	// zero/one-run windows without a divide-by-zero.
	let medianDurationMs = null;
	if (completedDurationsMs.length > 0) {
		const mid = Math.floor(completedDurationsMs.length / 2);
		medianDurationMs =
			completedDurationsMs.length % 2 !== 0 ? completedDurationsMs[mid] : Math.round((completedDurationsMs[mid - 1] + completedDurationsMs[mid]) / 2);
	}

	const medianDurationLabel = medianDurationMs === null ? NO_COMPLETED_RUNS_LABEL : formatDurationMs(medianDurationMs);

	// AC-11/AC-11a(iii) — one bar per windowed run (bar count matches windowCount 1:1),
	// oldest-to-newest (left-to-right reading order), mapped to success/error/neutral.
	const bars = window
		.slice()
		.reverse()
		.map(run => ({ key: run?.Id ?? run?.RunId, state: toBarState(run) }));

	return { windowCount, succeededCount, medianDurationMs, medianDurationLabel, bars };
};

export default computeRunHistoryStats;
